import useTheme from '@mui/styles/useTheme';
import { useTranslation } from 'react-i18next';
import DescriptionIcon from '@mui/icons-material/Description';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import React, { useState, useCallback, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import { Backdrop } from '@mui/material';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import first from 'lodash/first';
import last from 'lodash/last';
import map from 'lodash/map';
import filter from 'lodash/filter';
import matches from 'lodash/matches';
import parseInt from 'lodash/parseInt';
import { CircularProgress } from '@mui/material';
import numeral from 'numeral';
import { DASHBOARD_ORDERS } from '../../constants/Orders';
import OrdersLink from './components/OrdersLink';
import TotalSold from '../../components/Dashboard/TotalUnitSoldChart';
import { useCurrentUser } from '../../authentication/useAuth';
import { useDashboardApi } from '../../apis';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import { getPropertyValueByCaseInsensitiveName } from '../../utils/objectExtensions';

function Dashboard() {
	const isMountedRef = useIsMountedRef();
	const theme = useTheme();
	const { t } = useTranslation('dashboard', 'month');
	const currentUser = useCurrentUser()?.company;
	const { get: getDashboardData } = useDashboardApi();
	const [data, setData] = useState();
	const [isLoading, setIsLoading] = useState(false);
	const [isError, setIsError] = useState(false);

	const fetchUserToGA = useCallback(async () => {
		setIsLoading(true);
		try {
			if (isMountedRef.current) {
				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: 'logged_in_info',
					company_name: currentUser,
				});
			}
		} catch (err) {
			console.log(err);
		}
		setIsLoading(false);
	}, [isMountedRef, currentUser]);

	useEffect(() => {
		const translateLabel = (label) => {
			if (isEmpty(label)) {
				return '';
			}
			try {
				const m = label.split(' ')[0];
				const y = label.split(' ')[1];
				return `${t(`month:${m}`)} ${y}`;
			} catch {
				return label;
			}
		};
		setIsLoading(true);

		const dashboardData = {
			alertCount: 0,
			totalUnitsLabel: '',
			orderedLabelList: [],
			orderedList: [],
			shippedCount: 0,
			openOrderCount: 0,
		};

		Promise.all([getDashboardData()])
			.then((response) => {
				const dashboardRawData = get(response[0], 'data');
				const dashboardChartAndCountResp =
					typeof dashboardRawData === 'string'
						? JSON.parse(dashboardRawData)
						: dashboardRawData || {};
				const result1 = get(dashboardChartAndCountResp, 'result1');
				const result2 = get(dashboardChartAndCountResp, 'result2');

				if (!isEmpty(result1)) {
					let dateRangeLabel = '';
					const chartRows = filter(
						result1,
						(row) =>
							!isEmpty(getPropertyValueByCaseInsensitiveName(row, 'month'))
					);
					const labels = map(chartRows, (row) =>
						getPropertyValueByCaseInsensitiveName(row, 'month')
					);
					const ordersList = map(chartRows, (row) =>
						getPropertyValueByCaseInsensitiveName(row, 'aggregator')
					);

					if (!isEmpty(labels) && labels.length > 1) {
						dateRangeLabel = `${translateLabel(
							first(labels)
						)} - ${translateLabel(last(labels))}`;
					}

					dashboardData.orderedLabelList = labels;
					dashboardData.totalUnitsLabel = dateRangeLabel;
					dashboardData.orderedList = map(ordersList, (value) => {
						const parsedValue = parseInt(value);
						return Number.isNaN(parsedValue) ? 0 : parsedValue;
					});
				}

				if (!isEmpty(result2)) {
					const shipped = first(
						filter(result2, matches({ order_Status: DASHBOARD_ORDERS.SHIPPED }))
					);
					if (shipped) {
						dashboardData.shippedCount = isNumber(shipped.aggregator)
							? shipped.aggregator
							: 0;
					}

					const inProgress = first(
						filter(
							result2,
							matches({ order_Status: DASHBOARD_ORDERS.IN_PROGRESS })
						)
					);
					if (inProgress) {
						dashboardData.openOrderCount += isNumber(inProgress.aggregator)
							? inProgress.aggregator
							: 0;
					}
					const ordered = first(
						filter(result2, matches({ order_Status: DASHBOARD_ORDERS.ORDERED }))
					);
					if (ordered) {
						dashboardData.openOrderCount += isNumber(ordered.aggregator)
							? ordered.aggregator
							: 0;
					}
					const alert = first(
						filter(result2, matches({ order_Status: DASHBOARD_ORDERS.ALERT }))
					);
					if (alert) {
						dashboardData.alertCount += isNumber(alert.aggregator)
							? alert.aggregator
							: 0;
					}
				}

				setData(dashboardData);
				setIsLoading(false);
			})
			.catch((err) => {
				console.log(err);
				setIsError(true);
			});
	}, [getDashboardData, t]);

	useEffect(() => {
		fetchUserToGA();
	}, [fetchUserToGA, data]);

	return (
		<>
			<Backdrop
				sx={{
					color: 'primary',
					zIndex: theme.zIndex.drawer + 1,
					backgroundColor: 'transparent',
				}}
				open={!isError && !data && !isLoading}
			>
				<CircularProgress color="inherit" />
			</Backdrop>

			{!isLoading && data && (
				<Grid container spacing={3} p={1} mt={-4}>
					<Grid item xs={12} sm={6}>
						<Paper
							elevation={3}
							sx={{ padding: theme.spacing(2), height: '100%' }}
						>
							<Grid item xs={12} mt={-3}>
								<Grid
									container
									direction="row"
									justifyContent="space-evenly"
									alignItems="center"
									flexWrap="wrap"
									margin={theme.spacing(3, 0, 4, 0)}
									sx={{ textAlign: 'center' }}
								>
									<Grid item xs={12} mb={3.75}>
										<Typography variant="heading5">{t('shipments')}</Typography>
									</Grid>
									<OrdersLink
										total={numeral(data.alertCount).format('0,0')}
										title={t('alert', { count: data.alertCount })}
										statuses={['Alert']}
										data-testid="shipment_alert_count"
									/>
									<OrdersLink
										total={numeral(data.shippedCount).format('0,0')}
										title={t('shipped')}
										statuses={['Shipped']}
										data-testid="shipment_shipped_count"
									/>
								</Grid>
							</Grid>
						</Paper>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Paper
							elevation={3}
							sx={{ padding: theme.spacing(2), height: '100%' }}
						>
							<Grid item xs={12} mt={-3}>
								<Grid
									container
									direction="row"
									justifyContent="space-evenly"
									alignItems="center"
									flexWrap="wrap"
									margin={theme.spacing(3, 0, 4, 0)}
									sx={{ textAlign: 'center' }}
								>
									<Grid item xs={12} mb={3.75}>
										<Typography variant="heading5">
											{t('recent_activity')}
										</Typography>
									</Grid>

									<OrdersLink
										total={numeral(data.openOrderCount).format('0,0')}
										title={t('open_orders_plural', {
											count: data.openOrderCount,
										})}
										statuses={['In Progress', 'Ordered']}
										data-testid="recent_open_orders_count"
									/>

									<OrdersLink
										title={t('latest_documents')}
										icon={<DescriptionIcon fontSize="inherit" />}
										statuses={['Invoiced']}
										data-testid="recent_documents_count"
									/>
								</Grid>
							</Grid>
						</Paper>
					</Grid>
					<Grid item xs={12}>
						<Paper
							elevation={3}
							sx={{ padding: theme.spacing(2), height: '100%' }}
						>
							<TotalSold
								ordered={data.orderedList}
								labels={data.orderedLabelList}
								dateRangeLabel={data.totalUnitsLabel}
							/>
						</Paper>
					</Grid>
				</Grid>
			)}

			<Backdrop
				sx={{
					color: 'primary',
					zIndex: theme.zIndex.drawer + 1,
					backgroundColor: 'transparent',
				}}
				open={isError}
			>
				{t('no_dashboard_found')}
			</Backdrop>
		</>
	);
}

export default Dashboard;
